import { mimeTypes } from '@/shared/constants';
import { type DriveFile } from '@/shared/DriveTypes';
import {
  ArrowDownward,
  ArrowUpward,
  DriveFileRenameOutline,
  PersonAddAlt1Outlined,
  Replay,
  SubdirectoryArrowRight,
  VerticalAlignBottom,
  VerticalAlignTop,
} from '@mui/icons-material';
import { Backdrop, Divider, Menu, MenuItem, Modal } from '@mui/material';
import cx from 'classnames';
import { bindMenu } from 'material-ui-popup-state/hooks';
import { useState } from 'react';
import { match, P } from 'ts-pattern';
import { Link } from 'wouter';
import { DriveCopy, DriveMove, Trash } from '~/elements/icons';
import { ConditionalToolTip } from '~/elements/Tooltip';
import { darkTheme, lightTheme } from '~/theme.css';
import { isBun, isTest, maxCopyAmount } from '~/utils/constants';
import { DrivePicker } from '~/utils/DrivePicker';
import { useTreeMenu, useUserFiles } from '~/utils/hooks/hooks';
import { getTitle, isMarkdownLink } from '~/utils/isMarkdownLink';
import { sort } from '~/utils/sort';
import { fileModalTypes, modalTypes, useModal, useOpenId, useStore, useTheme } from '~/utils/store';
import { delayWithResolvers, getMimeTypeName, getViewInDriveUrl, isAFolder } from '~/utils/utils';
import { AddMenu } from '../AddMenu/AddMenu';
import { FileModal, type PageModalType } from '../FileModal/FileModal';
import { ShareModal } from '../ShareModal/ShareModal';
import getAllChildren from './getAllChildren';
import { NestedMenuItem } from './NestedMenuItem';
import * as styles from './Tree.css';
import { useCopy, useCreate, useMove, useMutateFile, useTreeMenuEffects } from './treeMenuUtils';

const getParents = (file: DriveFile | undefined) => {
  if (!file?.parents) throw new Error('parents not defined');
  return isAFolder(file) ? ([file.id] satisfies [string]) : file.parents;
};

/**
 * Handles the menus for the tree.
 * @param props
 * @param props.currentFile - The file that is currently highlighted in the tree, used for keyboard shortcuts
 */
export const TreeMenu = ({
  currentFile,
  files,
  wiki,
  wikis,
  closedByDefault,
}: {
  currentFile: DriveFile | undefined;
  files: DriveFile[];
  wiki: DriveFile;
  wikis: DriveFile[];
  closedByDefault: boolean;
}) => {
  const { addMenu, moreMenu } = useTreeMenu();
  const theme = useTheme();
  const { data: userFiles = [] } = useUserFiles();
  // const userFiles: DriveFile[] = [];
  // const { data: userFiles = [] } = useUserFilesData();
  const { openId, setOpenId } = useOpenId('tree');
  const setToast = useStore((store) => store.setToast);
  const [input, setInput] = useState<string | undefined>();
  const [linkURL, setLinkURL] = useState<string | undefined>();
  const closeMenus = (delay?: number) => {
    addMenu.close();
    moreMenu.close();
    setOpenId(undefined, delay);
  };
  const { openModal, closeModal, modalId, modalType } = useModal(openId, {
    onOpen: () => {
      closeMenus();
      setInput(undefined);
      setLinkURL(undefined);
    },
    onClose: () => {
      setInput(undefined);
      setLinkURL(undefined);
    },
  });
  const activeId = openId ?? modalId;
  // the file that is open in the menu or dialog, or the current file if no menu/dialog is open
  const activeFile = activeId ? (activeId === wiki.id ? wiki : files.find((o) => o.id === activeId)) : currentFile;
  const mutateFile = useMutateFile({ file: activeFile, files, wiki });
  const move = useMove({ files, file: activeFile, wiki, onMove: closeMenus });
  const copy = useCopy({ file: activeFile, files, wikiId: wiki.id });
  const create = useCreate({ files, wikiId: wiki.id });
  useTreeMenuEffects({ file: currentFile, wiki, files, closedByDefault, addMenu, moreMenu, move });
  const isWiki = openId === wiki.id;
  const siblings = files.filter((f) => f.parents?.[0] === activeFile?.parents?.[0]);
  const fileIndex = sort(siblings).findIndex((o) => o.id === openId);

  const children = activeFile?.mimeType === mimeTypes.folder ? getAllChildren(files, activeFile.id) : [];
  const canTrash =
    isBun ||
    (activeFile?.id !== wiki.id &&
      userFiles.some((o) => o.id === activeFile?.id) &&
      (children.length > 0 ? children.every((o) => userFiles.some((f) => f.id === o.id)) : true));

  return (
    <>
      {/* Add menu */}
      <AddMenu wiki={wiki} file={activeFile} files={files} popupState={addMenu} type="tree" />

      {/* More menu */}
      <Menu
        {...bindMenu(moreMenu)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transitionDuration={{
          enter: isTest ? 0 : 200,
          exit: 0,
        }}
        onClose={() => closeMenus()}
        className={cx({
          [darkTheme]: theme === 'dark',
          [lightTheme]: theme === 'light',
        })}
        slotProps={{
          paper: {
            className: styles.menu,
          },
        }}
      >
        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          onClick={() => {
            openModal('rename');
            closeMenus();
          }}
        >
          <DriveFileRenameOutline className={styles.menuIcon} style={{ fontSize: 20 }} />
          Rename
        </MenuItem>
        {isWiki && files.some((o) => o.properties?.index) && (
          <MenuItem
            className={styles.menuItem}
            focusVisibleClassName={styles.menuItemFocusVisible}
            onClick={() => {
              move('reset');
            }}
          >
            <Replay className={styles.menuIcon} style={{ fontSize: 22 }} />
            Reset all positions
          </MenuItem>
        )}
        {!isWiki && (
          <NestedMenuItem
            className={styles.menuItem}
            focusVisibleClassName={styles.menuItemFocusVisible}
            parentMenuOpen={moreMenu.isOpen}
            menuProps={{
              className: cx({
                [darkTheme]: theme === 'dark',
                [lightTheme]: theme === 'light',
              }),
              transitionDuration: {
                enter: isTest ? 0 : 200,
                exit: 0,
              },
              slotProps: {
                paper: {
                  className: styles.menu,
                },
              },
            }}
            label="Move"
            leftIcon={<SubdirectoryArrowRight className={styles.menuIcon} style={{ fontSize: 20 }} />}
          >
            {fileIndex > 0 && (
              <MenuItem
                className={styles.menuItem}
                focusVisibleClassName={styles.menuItemFocusVisible}
                onClick={() => {
                  move('up');
                }}
              >
                <ArrowUpward className={styles.menuIcon} style={{ fontSize: 20 }} />
                Move up
              </MenuItem>
            )}
            {fileIndex < siblings.length - 1 && (
              <MenuItem
                className={styles.menuItem}
                focusVisibleClassName={styles.menuItemFocusVisible}
                onClick={() => {
                  move('down');
                }}
              >
                <ArrowDownward className={styles.menuIcon} style={{ fontSize: 20 }} />
                Move down
              </MenuItem>
            )}
            <MenuItem
              className={styles.menuItem}
              focusVisibleClassName={styles.menuItemFocusVisible}
              onClick={async () => {
                closeMenus();
                await DrivePicker.load();
                DrivePicker.generatePicker({
                  type: 'folder',
                  wiki,
                  onAction: (data) => {
                    if (data.action !== 'picked') return;
                    const file = data.docs[0];
                    if (!file) throw new Error('File not defined');
                    void mutateFile({
                      addParents: file.id,
                    });
                  },
                });
                void DrivePicker.open('folder');
              }}
            >
              <DriveMove aria-hidden className={styles.menuIcon} style={{ fontSize: 22 }} />
              Move to...
            </MenuItem>
            {(siblings.length > 2 || activeFile?.properties?.index) && <Divider className={styles.divider} />}
            {fileIndex > 0 && siblings.length > 2 && (
              <MenuItem
                className={styles.menuItem}
                focusVisibleClassName={styles.menuItemFocusVisible}
                onClick={() => {
                  move('top');
                }}
              >
                <VerticalAlignTop className={styles.menuIcon} style={{ fontSize: 20 }} />
                Move to top
              </MenuItem>
            )}
            {fileIndex < siblings.length - 1 && siblings.length > 2 && (
              <MenuItem
                className={styles.menuItem}
                focusVisibleClassName={styles.menuItemFocusVisible}
                onClick={() => {
                  move('bottom');
                }}
              >
                <VerticalAlignBottom className={styles.menuIcon} style={{ fontSize: 20 }} />
                Move to bottom
              </MenuItem>
            )}
            {siblings.length > 1 && activeFile?.properties?.index && (
              <MenuItem
                className={styles.menuItem}
                focusVisibleClassName={styles.menuItemFocusVisible}
                onClick={() => {
                  move('reset');
                }}
              >
                <Replay className={styles.menuIcon} style={{ fontSize: 22 }} />
                Reset position
              </MenuItem>
            )}
          </NestedMenuItem>
        )}
        {activeFile?.id !== wiki.id && (
          <ConditionalToolTip
            enabled={children.length > maxCopyAmount}
            title={`Cannot copy folders with more than ${maxCopyAmount} items`}
          >
            <MenuItem
              disabled={children.length > maxCopyAmount}
              className={styles.menuItem}
              focusVisibleClassName={styles.menuItemFocusVisible}
              onClick={() => openModal('copy')}
            >
              <DriveCopy className={styles.menuIcon} style={{ fontSize: 20 }} />
              Make a copy
            </MenuItem>
          </ConditionalToolTip>
        )}
        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          onClick={() => {
            openModal('share');
          }}
        >
          <PersonAddAlt1Outlined className={styles.menuIcon} style={{ fontSize: 20 }} />
          Share
        </MenuItem>
        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          onClick={canTrash ? () => openModal('trash') : () => closeMenus(0)}
          {...(!canTrash && {
            component: Link,
            to: getViewInDriveUrl(activeFile) ?? '',
            target: '_blank',
          })}
        >
          {canTrash ? (
            <>
              <Trash aria-hidden className={styles.menuIcon} style={{ fontSize: 20 }} />
              Trash
            </>
          ) : (
            <>
              <Trash aria-hidden className={styles.menuIcon} style={{ fontSize: 20 }} />
              Trash in{' '}
              <img
                style={{ width: 16, height: 16, marginLeft: 2 }}
                src="https://www.gstatic.com/images/branding/product/1x/drive_48dp.png"
                alt="Drive logo"
              />
            </>
          )}
        </MenuItem>
      </Menu>

      {/* Modal */}
      <Modal
        open={Boolean(modalId && modalType)}
        onClose={closeModal}
        role="dialog"
        className={cx({
          [darkTheme]: theme === 'dark',
          [lightTheme]: theme === 'light',
        })}
        slots={{
          backdrop: Backdrop,
        }}
        slotProps={{
          backdrop: {
            timeout: 0,
          },
        }}
      >
        <div>
          {match(isMarkdownLink(activeFile) && modalType === 'rename' ? 'link' : modalType)
            .with('rename', (modalType) => (
              <FileModal
                title="Rename"
                modalType={modalType}
                onCancel={closeModal}
                onChange={(e) => setInput(e.target.value)}
                onSubmit={(e) => {
                  e.preventDefault();
                  const name = input?.trim();
                  if (!name || name === activeFile?.name) {
                    closeModal();
                    return;
                  }
                  void mutateFile({ name });
                  closeModal();
                }}
                defaultValue={activeFile?.name}
              />
            ))
            .with('share', () => (
              <ShareModal onCancel={closeModal} file={activeFile} wiki={wiki} files={files} wikis={wikis} />
            ))
            .with('trash', (modal) => (
              <FileModal
                title="Move to trash"
                modalType={modal}
                onCancel={closeModal}
                onSubmit={async () => {
                  const file = activeFile;
                  closeModal();
                  const p = delayWithResolvers(550);
                  void p.promise.then(() => setToast('Moving file to trash...')).catch(() => {});
                  await mutateFile({ trashed: true });
                  p.reject();
                  setToast({
                    message: 'File moved to trash',
                    action: async () => {
                      const p = delayWithResolvers(200);
                      void p.promise.then(() => setToast('Undoing...')).catch(() => {});
                      await mutateFile({ ...file, trashed: false });
                      p.reject();
                      setToast('Action undone');
                    },
                    actionLabel: 'Undo',
                  });
                }}
                description={
                  <>
                    Are you sure you want to move {activeFile?.name ? <b>"{getTitle(activeFile)}"</b> : 'this item'} to
                    the trash?
                  </>
                }
              />
            ))
            .with('copy', (modal) => (
              <FileModal
                title="Make a copy"
                modalType={modal}
                onCancel={closeModal}
                onSubmit={(e) => {
                  e.preventDefault();
                  copy();
                  closeModal();
                }}
                description="Are you sure you want to make a copy of this item?"
              />
            ))
            .with('link', (modal) =>
              ((markdownLink) => {
                if (modalType === 'rename') {
                  return (
                    <FileModal
                      title="Rename"
                      modalType={modal}
                      onCancel={closeModal}
                      onChange={(e) => setInput(e.target.value)}
                      onChangeURL={(e) => setLinkURL(e.target.value)}
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (modalType === 'rename') {
                          const name = `[${(input?.trim() ?? markdownLink?.name) || linkURL}](${linkURL ?? markdownLink?.url})`;
                          if (!name || name === activeFile?.name) {
                            return closeModal();
                          }
                          void mutateFile({ name });
                          return closeModal();
                        }
                      }}
                      defaultValue={markdownLink?.name}
                      defaultURLValue={markdownLink?.url}
                    />
                  );
                }
                return (
                  <FileModal
                    title="New link"
                    modalType={modal}
                    onCancel={closeModal}
                    onChange={(e) => setInput(e.target.value)}
                    onChangeURL={(e) => setLinkURL(e.target.value)}
                    onSubmit={(e) => {
                      e.preventDefault();
                      const name = `[${input?.trim() || linkURL}](${linkURL})`;
                      create({
                        name,
                        parents: getParents(activeFile),
                        mimeType: mimeTypes.document,
                      });
                      closeModal();
                    }}
                    defaultValue={`Untitled ${modal}`}
                  />
                );
              })(isMarkdownLink(activeFile))
            )
            .with(
              P.when((modal): modal is PageModalType => fileModalTypes.includes(modal as PageModalType)),
              (modal) => (
                <FileModal
                  title={`New ${modal}`}
                  modalType={modal}
                  onCancel={closeModal}
                  onChange={(e) => setInput(e.target.value)}
                  onSubmit={(e) => {
                    e.preventDefault();
                    const mimeType = modalTypes[modal];
                    const name = input?.trim() ? input : `Untitled ${getMimeTypeName(mimeType)}`;
                    create({
                      name,
                      parents: getParents(activeFile),
                      mimeType,
                    });
                    closeModal();
                  }}
                  defaultValue={`Untitled ${getMimeTypeName(modalTypes[modal])}`}
                />
              )
            )
            .otherwise(() => null)}
        </div>
      </Modal>
    </>
  );
};
